import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { getStartOf, addTime, isAfterDate, stringifyDateToISO8601 } from '../../util/dates';
import { enumerateDateStrings } from '../../util/cartAvailability';
import { dayCountAvailableForBooking } from '../../config/configStripe';

// RAS-2511: pure predicate builders for the cart drawer's day-range picker. Kept
// separate from the component so they're directly unit-testable without mounting
// react-dates. `blockedDates` is RAS-2509's unified group blocked-dates Set;
// `minimumDays` is RAS-2510's group minimum (null/1 = no extra constraint beyond
// DateRangeInput.js's own built-in 0-night minimum for daily bookings).

const isoDate = (date, timeZone) => stringifyDateToISO8601(date, timeZone);

// Calendar days between two instants, counted IN the zone - an instant diff truncates
// a day short across a spring-forward DST change (n days minus 1h). Same rule the
// store page's cards use for their duration/price package.
const calendarDaysBetween = (from, to, timeZone) => {
  const inZone = d => (timeZone ? moment(d).tz(timeZone) : moment(d)).startOf('day');
  return inZone(to).diff(inZone(from), 'days');
};

/**
 * A calendar day is blocked if it hasn't been checked yet (RAS-2506 follow-up: the
 * group's availability is now fetched lazily, one month at a time, as the user
 * navigates - a day whose month hasn't been fetched is treated as blocked until
 * proven otherwise, the same safe default the listing page's own calendar uses for an
 * unfetched month), if RAS-2509 flags its date as actually blocked, OR - once a start
 * date is selected - if picking it as the end would produce a range shorter than
 * `minimumDays`. `day` is the INCLUSIVE calendar cell the user is looking at;
 * DateRangeInput.js converts an inclusive end-day click to an exclusive
 * bookingDates.endDate (dayClicked + 1) before it ever reaches this app's cart
 * storage, so "nights from start through this inclusive day" = diff(day, start) + 1.
 */
export const buildIsDayBlocked = (blockedDates, checkedDates, timeZone, minimumDays, startDate) => day => {
  const iso = isoDate(day, timeZone);
  if (!checkedDates.has(iso)) return true;
  if (blockedDates.has(iso)) return true;

  if (startDate && minimumDays > 1) {
    const dayStart = getStartOf(day, 'day', timeZone);
    const rangeStart = getStartOf(startDate, 'day', timeZone);
    if (isAfterDate(dayStart, rangeStart) || moment(dayStart).isSame(rangeStart)) {
      const inclusiveDays = calendarDaysBetween(rangeStart, dayStart, timeZone) + 1;
      if (inclusiveDays < minimumDays) return true;
    }
  }

  return false;
};

/**
 * A day is outside the selectable range if it's in the past or beyond the
 * marketplace's booking look-ahead window (dayCountAvailableForBooking, same
 * constant the listing page's own booking calendar uses - see configStripe.js's
 * comment on why: Stripe holds funds up to 90 days after charge).
 */
export const buildIsOutsideRange = timeZone => {
  const today = getStartOf(new Date(), 'day', timeZone);
  const lastBookable = addTime(today, dayCountAvailableForBooking, 'days', timeZone);
  return day => {
    const d = getStartOf(day, 'day', timeZone);
    return isAfterDate(today, d) || isAfterDate(d, lastBookable);
  };
};

/**
 * True if any date strictly between startDate and endDate (both exclusive) is
 * blocked, OR hasn't been checked yet (see buildIsDayBlocked - same conservative
 * default) - used by DateRangeInput to reject a range that jumps over a blocked day.
 */
export const buildIsBlockedBetween = (blockedDates, checkedDates, timeZone) => (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  const dayAfterStart = addTime(getStartOf(startDate, 'day', timeZone), 1, 'days', timeZone);
  return enumerateDateStrings(dayAfterStart, endDate, timeZone).some(
    d => !checkedDates.has(d) || blockedDates.has(d)
  );
};

/**
 * Apply-button validity: a complete range, fully checked and non-blocked, that meets
 * the group minimum. `startDate`/`endDate` follow this app's exclusive-end convention
 * (same shape as cart items' bookingDates).
 */
export const isSelectionValid = (startDate, endDate, blockedDates, checkedDates, timeZone, minimumDays) => {
  if (!startDate || !endDate) return false;

  const days = calendarDaysBetween(startDate, endDate, timeZone);
  if (days <= 0) return false;
  if (minimumDays && days < minimumDays) return false;

  const rangeDates = enumerateDateStrings(startDate, endDate, timeZone);
  return !rangeDates.some(d => !checkedDates.has(d) || blockedDates.has(d));
};

/**
 * RAS-2511's "no available dates for this period" edge case: true only if no run of
 * consecutive unblocked days at least `minimumDays` long exists anywhere in the
 * booking look-ahead window. Deliberately NOT checkedDates-gated like the predicates
 * above - this runs right after the picker's first (narrow, RAS-2506-follow-up lazy)
 * fetch, when most of the year-long window is still unchecked. Gating it the same way
 * would make it report "no available dates" almost every time regardless of actual
 * availability, since only the first month or two would ever be marked checked at
 * that point. Treating unchecked days as available here is a deliberate, optimistic
 * exception scoped to this one pre-check.
 */
export const hasAnySelectableRange = (blockedDates, timeZone, minimumDays = 1) => {
  const today = getStartOf(new Date(), 'day', timeZone);
  const lastBookable = addTime(today, dayCountAvailableForBooking, 'days', timeZone);
  const windowDates = enumerateDateStrings(today, addTime(lastBookable, 1, 'days', timeZone), timeZone);

  let run = 0;
  for (const d of windowDates) {
    if (blockedDates.has(d)) {
      run = 0;
      continue;
    }
    run += 1;
    if (run >= Math.max(minimumDays, 1)) return true;
  }
  return false;
};
